import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { clinical, radiology } from "../lib/api.ts";
import type { NavItem } from "../lib/nav.ts";

/**
 * Tiny count bubble next to a nav entry. Nav items opt in through their
 * `badge` field (data-driven, like the rest of nav.ts) so the desktop header,
 * the overflow menu and the mobile drawer all show the same signal without
 * each inventing its own query.
 */
export function NavItemBadge({ item }: { item: NavItem }) {
	if (item.badge === "rx_pending") return <PendingRxBadge />;
	if (item.badge === "tasks_open") return <OpenTasksBadge />;
	if (item.badge === "results_inbox") return <ResultsInboxBadge />;
	if (item.badge === "radiology_incoming") return <RadiologyIncomingBadge />;
	if (item.badge === "critical_alerts") return <CriticalAlertsBadge />;
	return null;
}

/**
 * Incoming radiology referrals still awaiting a finalized report. Shares the
 * cache key the incoming worklist invalidates, so reporting a study drops the
 * bubble. Surfaces STAT referrals that would otherwise hide under "More".
 */
function RadiologyIncomingBadge() {
	const { t } = useTranslation();
	const q = useQuery({
		queryKey: ["radiology-incoming", "list"],
		queryFn: () => radiology.incoming(),
		staleTime: 60_000,
		refetchInterval: 120_000,
	});
	const pending = (q.data ?? []).filter((o) => !o.report_finalized).length;
	if (pending === 0) return null;
	return (
		<span
			aria-label={t("nav.radiology_incoming_badge")}
			className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] rounded-full bg-red-500 text-white text-xs font-bold px-1 tabular-nums"
		>
			{pending > 9 ? "9+" : pending}
		</span>
	);
}

/**
 * Un-notified critical lab values — a patient-safety count. Shares the
 * ["critical-alerts"] cache the alerts page invalidates after notify/ack, so
 * the bubble clears the moment the value is actioned.
 */
function CriticalAlertsBadge() {
	const { t } = useTranslation();
	const q = useQuery({
		queryKey: ["critical-alerts", "list", "pending"],
		queryFn: () =>
			clinical.listCriticalAlerts({ status: "pending", limit: 200 }),
		staleTime: 30_000,
		refetchInterval: 60_000,
	});
	const pending = (q.data ?? []).length;
	if (pending === 0) return null;
	return (
		<span
			aria-label={t("nav.critical_alerts_badge")}
			className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] rounded-full bg-red-500 text-white text-xs font-bold px-1 tabular-nums"
		>
			{pending > 9 ? "9+" : pending}
		</span>
	);
}

/**
 * Unreviewed results (critical + abnormal + radiology) for the doctor's
 * results inbox. Shares the cache key the inbox page invalidates after
 * review/verify actions, so the bubble drops the moment work is signed off.
 */
function ResultsInboxBadge() {
	const { t } = useTranslation();
	const q = useQuery({
		queryKey: ["results-inbox-counts"],
		queryFn: () => clinical.resultsInboxCounts(),
		staleTime: 60_000,
		refetchInterval: 120_000,
	});
	const total = q.data?.total ?? 0;
	if (total === 0) return null;
	return (
		<span
			aria-label={t("nav.results_inbox_badge")}
			className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] rounded-full bg-red-500 text-white text-xs font-bold px-1 tabular-nums"
		>
			{total > 9 ? "9+" : total}
		</span>
	);
}

/** Open tasks (mine + unassigned pool) — same cache key as the tasks inbox. */
function OpenTasksBadge() {
	const { t } = useTranslation();
	const q = useQuery({
		queryKey: ["task-counts"],
		queryFn: () => clinical.taskCounts(),
		staleTime: 60_000,
		refetchInterval: 120_000,
	});
	const open = (q.data?.mine ?? 0) + (q.data?.unassigned ?? 0);
	if (open === 0) return null;
	return (
		<span
			aria-label={t("nav.tasks_badge")}
			className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] rounded-full bg-red-500 text-white text-xs font-bold px-1 tabular-nums"
		>
			{open > 9 ? "9+" : open}
		</span>
	);
}

/** Pending (not yet dispensed) incoming prescriptions for a pharmacy tenant. */
function PendingRxBadge() {
	const { t } = useTranslation();
	// Shares the page/widget cache key, so accepting or dispensing a referral
	// invalidates the badge too. Polling keeps it fresh while the pharmacist
	// works elsewhere (the referral arrives from another tenant).
	const q = useQuery({
		queryKey: ["incoming-prescriptions"],
		queryFn: () => clinical.incomingPrescriptions(),
		staleTime: 60_000,
		refetchInterval: 120_000,
	});
	const pending = (q.data ?? []).filter((r) => !r.dispensed_at).length;
	if (pending === 0) return null;
	return (
		<span
			aria-label={t("nav.rx_pending_badge")}
			className="inline-flex items-center justify-center min-w-[1.1rem] h-[1.1rem] rounded-full bg-red-500 text-white text-xs font-bold px-1 tabular-nums"
		>
			{pending > 9 ? "9+" : pending}
		</span>
	);
}
